/**
 * AI-Powered Customer Support Service
 * Provides intelligent customer support using AI to handle common queries
 * and escalate complex issues to human agents
 */

import aiService from './aiService';
import websiteContextService from './websiteContextService';

class AICustomerSupport {
    constructor() {
        this.conversationHistory = JSON.parse(localStorage.getItem('ai_support_history') || '[]');
        this.userContext = JSON.parse(localStorage.getItem('ai_user_context') || '{}');
        this.escalationKeywords = [
            'refund', 'cancel', 'complaint', 'problem', 'issue', 'wrong', 'bad', 'terrible',
            'manager', 'supervisor', 'human', 'agent', 'representative'
        ];
        this.commonQueries = {
            'order_status': ['order', 'status', 'tracking', 'delivery', 'when'],
            'menu_inquiry': ['menu', 'dish', 'ingredients', 'allergens', 'nutrition'],
            'payment': ['payment', 'billing', 'charge', 'refund', 'money'],
            'delivery': ['delivery', 'address', 'time', 'location', 'area'],
            'account': ['account', 'profile', 'login', 'password', 'signup'],
            'general': ['help', 'support', 'question', 'information']
        };
    }

    /**
     * Process customer support query
     */
    async processQuery(message, context = {}) {
        try {
            // Analyze the query to determine intent and category
            const analysis = await this.analyzeQuery(message, context);
            
            // Check if escalation is needed
            if (this.shouldEscalate(message, analysis)) {
                return this.getEscalationResponse(message, analysis);
            }

            // Generate AI response based on analysis
            const response = await this.generateResponse(message, analysis, context);
            
            // Save conversation
            this.saveConversation(message, response, analysis);
            
            return {
                success: true,
                response: response,
                analysis: analysis,
                needsEscalation: false,
                context: context
            };
        } catch (error) {
            console.error('AI customer support error:', error);
            return this.getFallbackResponse(message);
        }
    }

    /**
     * Analyze customer query to understand intent
     */
    async analyzeQuery(message, context) {
        // Get relevant website context
        const relevantInfo = websiteContextService.getRelevantInfo(message);
        const websiteContext = websiteContextService.getWebsiteContext();
        
        const prompt = `Analyze this customer support query for a food delivery platform:

Message: "${message}"
Context: ${JSON.stringify(context)}
Website Context: ${JSON.stringify(websiteContext)}
Relevant Info: ${JSON.stringify(relevantInfo)}

Please provide:
1. Query category (order_status, menu_inquiry, payment, delivery, account, contact, general)
2. Intent (question, complaint, request, information, order_processing)
3. Urgency level (low, medium, high)
4. Key entities mentioned (order_id, dish_name, etc.)
5. Sentiment (positive, neutral, negative)
6. Suggested response approach
7. Whether this is order-related and needs processing guidance

Return as JSON with these fields.`;

        try {
            const response = await aiService.chatWithAI(prompt, { type: 'support_analysis' });
            if (response.success) {
                return this.parseAIResponse(response.data);
            }
        } catch (error) {
            console.error('Query analysis error:', error);
        }

        return this.getFallbackAnalysis(message);
    }

    /**
     * Generate AI response based on analysis
     */
    async generateResponse(message, analysis, context) {
        const category = analysis.category || 'general';
        
        switch (category) {
            case 'order_status':
                return await this.handleOrderStatusQuery(message, analysis, context);
            case 'menu_inquiry':
                return await this.handleMenuInquiry(message, analysis, context);
            case 'payment':
                return await this.handlePaymentQuery(message, analysis, context);
            case 'delivery':
                return await this.handleDeliveryQuery(message, analysis, context);
            case 'account':
                return await this.handleAccountQuery(message, analysis, context);
            case 'contact':
                return await this.handleContactQuery(message, analysis, context);
            default:
                return await this.handleGeneralQuery(message, analysis, context);
        }
    }

    /**
     * Handle order status queries
     */
    async handleOrderStatusQuery(message, analysis, context) {
        const websiteContext = websiteContextService.getWebsiteContext();
        const orderGuidance = websiteContextService.getOrderProcessingGuidance();
        
        const prompt = websiteContextService.generateContextPrompt(message, 'order_status');

        try {
            const response = await aiService.chatWithAI(prompt, { type: 'order_support' });
            if (response.success) {
                return response.data;
            }
        } catch (error) {
            console.error('Order status response error:', error);
        }

        return `I'd be happy to help you with your order! You can track your order in real-time through our app or website using your order ID. 

For order assistance, contact us at:
📧 ${websiteContext.contact.email}
📞 ${websiteContext.contact.phone}

Our support team is available ${websiteContext.contact.businessHours.support} to help you with any order-related queries.`;
    }

    /**
     * Filter out technical content from AI responses
     */
    filterTechnicalContent(response) {
        // Remove code blocks
        let filtered = response.replace(/```[\s\S]*?```/g, '');
        
        // Remove code snippets
        filtered = filtered.replace(/`[^`]+`/g, '');
        
        // Remove programming-related content
        const programmingPatterns = [
            /class\s+\w+\s*{[\s\S]*?}/g,
            /def\s+\w+\([^)]*\):[\s\S]*?(?=\n\w|\n\n|$)/g,
            /function\s+\w+\([^)]*\)\s*{[\s\S]*?}/g,
            /import\s+[\w\s,]+/g,
            /from\s+[\w\s]+\s+import/g,
            /console\.log\([^)]*\)/g,
            /print\([^)]*\)/g,
            /# [^\n]*/g,
            /\/\/ [^\n]*/g,
            /\/\*[\s\S]*?\*\//g
        ];
        
        programmingPatterns.forEach(pattern => {
            filtered = filtered.replace(pattern, '');
        });
        
        // Clean up extra whitespace
        filtered = filtered.replace(/\n\s*\n\s*\n/g, '\n\n');
        filtered = filtered.trim();
        
        // If response becomes too short or empty, provide fallback
        if (filtered.length < 50) {
            return "I'm here to help with food delivery questions! How can I assist you with our menu, orders, or delivery services?";
        }
        
        return filtered;
    }

    /**
     * Handle menu inquiries
     */
    async handleMenuInquiry(message, analysis, context) {
        const websiteContext = websiteContextService.getWebsiteContext();
        
        // Extract dish-specific context if available
        let dishContext = '';
        if (context && context.menuItem) {
            dishContext = `
DISH CONTEXT:
- Name: ${context.menuItem.name}
- Description: ${context.menuItem.description || 'No description available'}
- Price: ₹${context.menuItem.price}
- Ingredients: ${Array.isArray(context.menuItem.ingredients) ? context.menuItem.ingredients.join(', ') : 'Not specified'}
- Dietary Info: ${context.menuItem.is_vegetarian ? 'Vegetarian' : 'Non-vegetarian'}${context.menuItem.is_spicy ? ', Spicy' : ''}${context.menuItem.is_vegan ? ', Vegan' : ''}
- Preparation Time: ${context.menuItem.preparation_time || 'Not specified'} minutes
- Vendor: ${context.menuItem.vendor?.business_name || 'Not specified'}
- Category: ${context.menuItem.category?.name || 'Not specified'}
`;
        }
        
        const prompt = `You are a customer support representative for ${websiteContext.name}, a food delivery platform. You ONLY provide information about food delivery, menu items, orders, and restaurant services.

WEBSITE CONTEXT:
${JSON.stringify(websiteContext, null, 2)}

${dishContext}

USER QUERY: "${message}"

CRITICAL INSTRUCTIONS:
1. You are ONLY a food delivery customer support agent - NEVER provide coding, programming, technical, or unrelated advice
2. NEVER generate code, scripts, programs, or technical solutions - this is strictly forbidden
3. If the user asks about coding, programming, or technical topics, politely redirect them to food delivery topics
4. If asking about a specific dish (context provided above), focus your response on that dish only
5. Provide accurate information based on the dish context and website information
6. Be helpful and professional within food delivery context only
7. If asking about ingredients, allergens, or dietary info, use the dish context
8. If asking about pricing, delivery, or ordering, provide relevant information
9. Always stay within the context of our food delivery platform
10. For dish-specific questions, reference the exact dish information provided
11. If user asks about non-food topics, respond: "I'm here to help with food delivery questions. How can I assist you with our menu, orders, or delivery services?"
12. NEVER provide programming examples, code snippets, or technical implementations

RESPONSE FORMAT: Only provide food delivery related information. No code, no technical advice, no programming help.

Please provide a helpful response based on the user's query and the dish context, staying strictly within food delivery topics.`;

        try {
            const response = await aiService.chatWithAI(prompt, { type: 'menu_support' });
            if (response.success) {
                // Filter out any code snippets or technical content
                let filteredResponse = this.filterTechnicalContent(response.data);
                return filteredResponse;
            }
        } catch (error) {
            console.error('Menu inquiry response error:', error);
        }

        // Check if query is about coding/programming/technical topics
        const technicalKeywords = [
            'code', 'programming', 'python', 'javascript', 'class', 'function', 'variable', 
            'algorithm', 'database', 'api', 'html', 'css', 'react', 'node', 'php', 'sql',
            'generate', 'create', 'write', 'build', 'develop', 'implement', 'script',
            'program', 'software', 'application', 'framework', 'library', 'module',
            'syntax', 'compile', 'debug', 'deploy', 'repository', 'git', 'github',
            'json', 'xml', 'yaml', 'markdown', 'docker', 'kubernetes', 'aws', 'azure'
        ];
        const isTechnicalQuery = technicalKeywords.some(keyword => 
            message.toLowerCase().includes(keyword.toLowerCase())
        );

        if (isTechnicalQuery) {
            return `I'm here to help with food delivery questions! I can assist you with:

🍽️ **Menu Information** - Ingredients, dietary info, pricing, preparation methods
📦 **Ordering** - How to place orders, delivery options, customization
🚚 **Delivery** - Delivery times, areas, tracking, delivery fees
💳 **Payment** - Payment methods, billing, refunds
📞 **Support** - Contact information, help with orders, complaints
🍴 **Dish Details** - Spice levels, portion sizes, allergens, cooking time

For technical or programming questions, please contact our technical support team at ${websiteContext.contact.email}.

How can I help you with our food delivery services today?`;
        }

        // Fallback response with dish context
        if (context && context.menuItem) {
            return `I'd be happy to help you with information about ${context.menuItem.name}!

${context.menuItem.description ? `Description: ${context.menuItem.description}` : ''}
Price: ₹${context.menuItem.price}
${Array.isArray(context.menuItem.ingredients) ? `Ingredients: ${context.menuItem.ingredients.join(', ')}` : ''}
Preparation Time: ${context.menuItem.preparation_time || 'Not specified'} minutes
${context.menuItem.is_vegetarian ? '✅ Vegetarian' : '❌ Non-vegetarian'}${context.menuItem.is_spicy ? ' 🌶️ Spicy' : ''}${context.menuItem.is_vegan ? ' 🌱 Vegan' : ''}

For more information or to place an order, contact us at:
📧 ${websiteContext.contact.email}
📞 ${websiteContext.contact.phone}`;
        }

        return `I'd be happy to help you with menu information! You can browse our extensive selection of restaurants and dishes on our platform. Each menu item includes detailed information about ingredients, allergens, and nutritional facts.

Key features:
• Wide variety of cuisines and restaurants
• Detailed ingredient lists and allergen information
• Nutritional information for health-conscious choices
• AI-powered dish recommendations

For specific menu questions, contact us at:
📧 ${websiteContext.contact.email}
📞 ${websiteContext.contact.phone}`;
    }

    /**
     * Handle payment queries
     */
    async handlePaymentQuery(message, analysis, context) {
        const websiteContext = websiteContextService.getWebsiteContext();
        
        const prompt = websiteContextService.generateContextPrompt(message, 'payment');

        try {
            const response = await aiService.chatWithAI(prompt, { type: 'payment_support' });
            if (response.success) {
                return response.data;
            }
        } catch (error) {
            console.error('Payment query response error:', error);
        }

        return `I understand your concern about payment. We offer multiple secure payment options:

💳 **Online Payments:**
${websiteContext.paymentMethods.online.join(', ')}

💰 **Offline Payments:**
${websiteContext.paymentMethods.offline.join(', ')}

🔒 **Security:** ${websiteContext.paymentMethods.security}

If you're experiencing a payment issue, please contact our support team:
📧 ${websiteContext.contact.email}
📞 ${websiteContext.contact.phone}

We're available ${websiteContext.contact.businessHours.support} to help resolve any payment concerns.`;
    }

    /**
     * Handle delivery queries
     */
    async handleDeliveryQuery(message, analysis, context) {
        const websiteContext = websiteContextService.getWebsiteContext();
        
        const prompt = websiteContextService.generateContextPrompt(message, 'delivery');

        try {
            const response = await aiService.chatWithAI(prompt, { type: 'delivery_support' });
            if (response.success) {
                return response.data;
            }
        } catch (error) {
            console.error('Delivery query response error:', error);
        }

        return `I can help you with delivery information! Here are our delivery details:

🚚 **Delivery Areas:** ${websiteContext.deliveryAreas.primary.join(', ')}
⏰ **Delivery Time:** ${websiteContext.services.foodDelivery.deliveryTime}
💰 **Delivery Fee:** ${websiteContext.services.foodDelivery.deliveryFee}
📦 **Minimum Order:** ${websiteContext.services.foodDelivery.minimumOrder}

**Key Features:**
• Real-time order tracking
• Multiple delivery addresses
• Delivery time estimates
• Live delivery updates

**Delivery Hours:** ${websiteContext.contact.businessHours.delivery}

For delivery issues or questions, contact us:
📧 ${websiteContext.contact.email}
📞 ${websiteContext.contact.phone}`;
    }

    /**
     * Handle account queries
     */
    async handleAccountQuery(message, analysis, context) {
        const websiteContext = websiteContextService.getWebsiteContext();
        
        const prompt = websiteContextService.generateContextPrompt(message, 'account');

        try {
            const response = await aiService.chatWithAI(prompt, { type: 'account_support' });
            if (response.success) {
                return response.data;
            }
        } catch (error) {
            console.error('Account query response error:', error);
        }

        return `I can help you with your account! Here's what you can do:

🔐 **Account Features:**
${Object.values(websiteContext.accountFeatures).join('\n• ')}

**Common Account Issues:**
• **Login Problems:** Use 'Forgot Password' link or contact support
• **Profile Updates:** Go to 'My Account' in the menu
• **Registration:** Use valid email and strong password
• **Order History:** View past orders in 'My Orders' section

**Need Help?** Contact our support team:
📧 ${websiteContext.contact.email}
📞 ${websiteContext.contact.phone}

We're available ${websiteContext.contact.businessHours.support} to assist you.`;
    }

    /**
     * Handle general queries
     */
    async handleGeneralQuery(message, analysis, context) {
        const websiteContext = websiteContextService.getWebsiteContext();
        
        const prompt = websiteContextService.generateContextPrompt(message, 'general');

        try {
            const response = await aiService.chatWithAI(prompt, { type: 'general_support' });
            if (response.success) {
                return response.data;
            }
        } catch (error) {
            console.error('General query response error:', error);
        }

        return `Hello! Welcome to ${websiteContext.name}! I'm here to help you with:

🍽️ **Our Services:**
• Food delivery from local restaurants
• Catering services for events
• Vendor onboarding for restaurants

📞 **Contact Information:**
• Email: ${websiteContext.contact.email}
• Phone: ${websiteContext.contact.phone}
• WhatsApp: ${websiteContext.contact.whatsapp}

⏰ **Support Hours:** ${websiteContext.contact.businessHours.support}

I can help you with orders, menu information, delivery, payments, account questions, and more. What would you like to know?`;
    }

    /**
     * Handle contact queries
     */
    async handleContactQuery(message, analysis, context) {
        const websiteContext = websiteContextService.getWebsiteContext();
        
        const prompt = websiteContextService.generateContextPrompt(message, 'contact');

        try {
            const response = await aiService.chatWithAI(prompt, { type: 'contact_support' });
            if (response.success) {
                return response.data;
            }
        } catch (error) {
            console.error('Contact query response error:', error);
        }

        return `Here's how you can reach us:

📧 **Email:** ${websiteContext.contact.email}
📞 **Phone:** ${websiteContext.contact.phone}
💬 **WhatsApp:** ${websiteContext.contact.whatsapp}
📍 **Address:** ${websiteContext.contact.address}

⏰ **Business Hours:**
• Support: ${websiteContext.contact.businessHours.support}
• Delivery: ${websiteContext.contact.businessHours.delivery}
• Customer Service: ${websiteContext.contact.businessHours.customerService}

We're here to help with any questions or concerns you may have!`;
    }

    /**
     * Check if query should be escalated to human agent
     */
    shouldEscalate(message, analysis) {
        const lowerMessage = message.toLowerCase();
        
        // Check for escalation keywords
        const hasEscalationKeywords = this.escalationKeywords.some(keyword => 
            lowerMessage.includes(keyword)
        );
        
        // Check sentiment
        const isNegative = analysis.sentiment === 'negative';
        
        // Check urgency
        const isHighUrgency = analysis.urgency === 'high';
        
        // Check if it's a complex issue
        const isComplex = analysis.intent === 'complaint' || 
                         lowerMessage.includes('manager') || 
                         lowerMessage.includes('supervisor');
        
        return hasEscalationKeywords || isNegative || isHighUrgency || isComplex;
    }

    /**
     * Get escalation response
     */
    getEscalationResponse(message, analysis) {
        return {
            success: true,
            response: "I understand your concern and want to make sure you get the best help possible. Let me connect you with one of our human support agents who can provide personalized assistance. Please hold on while I transfer you.",
            analysis: analysis,
            needsEscalation: true,
            escalationReason: this.getEscalationReason(message, analysis)
        };
    }

    /**
     * Get escalation reason
     */
    getEscalationReason(message, analysis) {
        if (analysis.sentiment === 'negative') return 'Negative sentiment detected';
        if (analysis.urgency === 'high') return 'High urgency issue';
        if (this.escalationKeywords.some(k => message.toLowerCase().includes(k))) return 'Escalation keywords detected';
        return 'Complex issue requiring human assistance';
    }

    /**
     * Save conversation to history
     */
    saveConversation(message, response, analysis) {
        const conversation = {
            message: message,
            response: response,
            analysis: analysis,
            timestamp: new Date().toISOString(),
            needsEscalation: analysis.needsEscalation || false
        };

        this.conversationHistory.unshift(conversation);
        
        // Keep only last 50 conversations
        if (this.conversationHistory.length > 50) {
            this.conversationHistory = this.conversationHistory.slice(0, 50);
        }

        localStorage.setItem('ai_support_history', JSON.stringify(this.conversationHistory));
    }

    /**
     * Get conversation history
     */
    getConversationHistory(limit = 10) {
        return this.conversationHistory.slice(0, limit);
    }

    /**
     * Get support insights
     */
    getSupportInsights() {
        const totalQueries = this.conversationHistory.length;
        const escalatedQueries = this.conversationHistory.filter(c => c.needsEscalation).length;
        const categories = {};
        
        this.conversationHistory.forEach(conv => {
            const category = conv.analysis?.category || 'unknown';
            categories[category] = (categories[category] || 0) + 1;
        });

        return {
            total_queries: totalQueries,
            escalated_queries: escalatedQueries,
            escalation_rate: totalQueries > 0 ? (escalatedQueries / totalQueries * 100).toFixed(2) + '%' : '0%',
            category_breakdown: categories,
            recent_queries: this.conversationHistory.slice(0, 5)
        };
    }

    /**
     * Parse AI response that might be wrapped in markdown code blocks
     */
    parseAIResponse(response) {
        try {
            // Remove markdown code block formatting if present
            let cleanResponse = response.trim();
            
            // Remove ```json and ``` if present
            if (cleanResponse.startsWith('```json')) {
                cleanResponse = cleanResponse.replace(/^```json\s*/, '').replace(/\s*```$/, '');
            } else if (cleanResponse.startsWith('```')) {
                cleanResponse = cleanResponse.replace(/^```\s*/, '').replace(/\s*```$/, '');
            }
            
            // Try to parse as JSON
            return JSON.parse(cleanResponse);
        } catch (error) {
            console.error('Failed to parse AI response:', error);
            console.log('Raw response:', response);
            
            // If parsing fails, try to extract JSON-like content
            try {
                // Look for JSON-like patterns in the response
                const jsonMatch = response.match(/\{[\s\S]*\}/);
                if (jsonMatch) {
                    return JSON.parse(jsonMatch[0]);
                }
                
                // Look for array-like patterns
                const arrayMatch = response.match(/\[[\s\S]*\]/);
                if (arrayMatch) {
                    return JSON.parse(arrayMatch[0]);
                }
            } catch (secondError) {
                console.error('Secondary parsing also failed:', secondError);
            }
            
            // Return null if all parsing attempts fail
            return null;
        }
    }

    /**
     * Fallback analysis when AI is not available
     */
    getFallbackAnalysis(message) {
        const lowerMessage = message.toLowerCase();
        
        // Simple keyword matching for categories
        let category = 'general';
        if (this.commonQueries.order_status.some(k => lowerMessage.includes(k))) {
            category = 'order_status';
        } else if (this.commonQueries.menu_inquiry.some(k => lowerMessage.includes(k))) {
            category = 'menu_inquiry';
        } else if (this.commonQueries.payment.some(k => lowerMessage.includes(k))) {
            category = 'payment';
        } else if (this.commonQueries.delivery.some(k => lowerMessage.includes(k))) {
            category = 'delivery';
        } else if (this.commonQueries.account.some(k => lowerMessage.includes(k))) {
            category = 'account';
        } else if (lowerMessage.includes('contact') || lowerMessage.includes('phone') || 
                   lowerMessage.includes('email') || lowerMessage.includes('address') ||
                   lowerMessage.includes('support') || lowerMessage.includes('help')) {
            category = 'contact';
        }

        return {
            category: category,
            intent: 'question',
            urgency: 'medium',
            sentiment: 'neutral',
            entities: []
        };
    }

    /**
     * Fallback response when AI is not available
     */
    getFallbackResponse(message) {
        return {
            success: false,
            response: "I apologize, but I'm having trouble processing your request right now. Please try again in a moment, or contact our support team directly for immediate assistance.",
            analysis: this.getFallbackAnalysis(message),
            needsEscalation: false
        };
    }

    /**
     * Clear conversation history
     */
    clearHistory() {
        this.conversationHistory = [];
        localStorage.removeItem('ai_support_history');
    }

    /**
     * Update user context
     */
    updateUserContext(context) {
        this.userContext = { ...this.userContext, ...context };
        localStorage.setItem('ai_user_context', JSON.stringify(this.userContext));
    }
}

// Create singleton instance
const aiCustomerSupport = new AICustomerSupport();

export default aiCustomerSupport;
